import type { MouseEventHandler, ReactNode } from 'react';
import { Wrapper, Button } from './styled';
import type { CompanySubSection } from '@/pages/CompanyReport/hooks/useReportUrlHandler';
import { getSectionFromAnchor } from '@/pages/CompanyReport/useSection';
import { useTracking } from 'react-tracking';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import ArrowRightSolid from '@simplywallst/ui-core/icons/ArrowRightSolid';
import { Text, Box, useTheme } from '@simplywallst/ui-core';
import { useCompanyReportPageUrl } from '@/pages/CompanyReport/hooks/useCompanyReportPageUrl';
import { RiskReward as Icon } from '@/components/RiskReward';
import SupportSolid from '@simplywallst/ui-core/icons/SupportSolid';
import { useLocale } from '@/hooks/useLocale';
import { useI18nFeedback } from '@/hooks/useI18nFeedback';
import useUser from '@/hooks/useUser';
import { splitOnLastWord } from './utils';

export type Severity = 'minor' | 'major' | 'none' | null;

interface AnchorLinkProps {
  href?: string;
  onClick: MouseEventHandler<HTMLAnchorElement> | undefined;
  children: ReactNode;
}

export const AnchorLink = ({
  children,
  href,
  onClick,
  ...rest
}: AnchorLinkProps) => {
  const { mainText, lastWord } = splitOnLastWord(children);
  return (
    <Button
      as="a"
      href={href}
      styleType="softLink"
      onClick={onClick}
      className="group/arrow"
      {...rest}
    >
      {mainText + ' '}
      <span className="inline-block whitespace-nowrap">
        {lastWord}
        <ArrowRightSolid className="-ml-x0_25 -mr-x1 fill-subtle align-middle group-hover/arrow:rotate-90" />
      </span>
    </Button>
  );
};

interface Props {
  name: string;
  index: number;
  severity?: Severity;
  description?: string | null;
  hash?: CompanySubSection;
}

const RiskReward = ({
  name,
  index,
  severity = null,
  description,
  hash,
}: Props) => {
  const { loadSection } = useVisibleSectionsContext();
  const companyUrl = useCompanyReportPageUrl();
  const section = hash && getSectionFromAnchor(hash);
  const href =
    section && `${companyUrl}${section === 'summary' ? '' : `/${section}`}`;
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'risk_reward',
  });
  const theme = useTheme();
  const locale = useLocale();
  const makeFeedbackDrawer = useI18nFeedback(name);
  const user = useUser();
  return (
    <Track>
      <Wrapper data-cy-id={`report-statement-${name}`}>
        <div>
          <Icon severity={severity} />
        </div>
        {description && (
          <Box marginLeft="x0_75" marginTop="x0_25" marginTopLg="x0">
            <Text typography="small">
              {typeof hash === 'undefined' ? (
                <>{description}</>
              ) : (
                <AnchorLink
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    loadSection(hash === 'overview' ? 'summary' : hash);
                    trackEvent({
                      modifier: severity ? 'risk' : 'reward',
                      action: 'click',
                      properties: { name, index, severity },
                    });
                  }}
                >
                  {description}
                </AnchorLink>
              )}
              {locale !== 'en' && user.data.loggedIn && (
                <SupportSolid
                  fill={theme.color.textSubtle}
                  cursor="pointer"
                  onClick={makeFeedbackDrawer}
                />
              )}
            </Text>
          </Box>
        )}
      </Wrapper>
    </Track>
  );
};

export default RiskReward;
